import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import type { PmTag, PmTagColor, PmTask } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/** Whole class names, so Tailwind finds them. Each `--tag-*` is 3:1 on every surface a dot lands on (token-contrast.test.ts). */
const DOT: Record<PmTagColor, string> = {
  blue: "bg-[var(--tag-blue)]",
  green: "bg-[var(--tag-green)]",
  orange: "bg-[var(--tag-orange)]",
  purple: "bg-[var(--tag-purple)]",
  teal: "bg-[var(--tag-teal)]",
  red: "bg-[var(--tag-red)]",
  yellow: "bg-[var(--tag-yellow)]",
  gray: "bg-[var(--tag-gray)]",
};

/** The colour only supports the label, like a StatusChip's dot. */
export function TagDot({ color }: { color: PmTagColor }) {
  return <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", DOT[color])} />;
}

export function TagChip({ tag }: { tag: PmTag }) {
  return (
    <span className="bg-muted text-foreground inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium">
      <TagDot color={tag.color} />
      {tag.label}
    </span>
  );
}

/** A task's tags in the realm's order. */
export const tagsOfTask = (tags: PmTag[], task: Pick<PmTask, "tagIds">): PmTag[] => tags.filter((g) => task.tagIds.includes(g.id));

const sameList = (a: number[], b: number[]) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * The tag dropdown. Its menu stays open while ticking, so it holds the selection itself and hands over the final list once,
 * when it closes: a save per tick would send the second tick with the version the first one just replaced, and log a row per
 * tick. `onOpen` lets the caller capture the version the edit starts from. A realm without tags shows nothing.
 *
 * Enter ticks AND closes in one go, with the close reported first, so the handler of the close would still see the list from
 * before that tick. The hand-over therefore waits for the render after both.
 */
export function PmTagPicker({
  tags,
  selected,
  isDisabled,
  onOpen,
  onCommit,
}: {
  tags: PmTag[];
  selected: number[];
  isDisabled?: boolean;
  onOpen?: () => void;
  onCommit: (tagIds: number[]) => void;
}) {
  const { t } = useTranslation();
  const labelId = useId();
  const valueId = useId();
  const [draft, setDraft] = useState<number[] | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (open || draft === null) return;
    if (!sameList(draft, selected)) onCommit(draft);
    setDraft(null);
  }, [open, draft, selected, onCommit]);
  if (tags.length === 0) return null;
  const shown = draft ?? selected;
  const shownTags = tags.filter((g) => shown.includes(g.id));
  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="text-sm font-medium">
        {t("pmBoard.tags.label")}
      </span>
      <DropdownMenuTrigger
        onOpenChange={(next) => {
          setOpen(next);
          if (next) {
            setDraft(selected);
            onOpen?.();
          }
        }}
      >
        <Button variant="outline" aria-labelledby={`${labelId} ${valueId}`} isDisabled={isDisabled ?? false} className="h-auto min-h-9 w-full justify-between whitespace-normal">
          <span id={valueId} className="flex min-w-0 flex-wrap gap-1.5">
            {shownTags.length > 0 ? shownTags.map((g) => <TagChip key={g.id} tag={g} />) : <span className="text-muted-foreground font-normal">{t("pmBoard.tags.none")}</span>}
          </span>
          <ChevronDown aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
        </Button>
        <DropdownMenu
          aria-labelledby={labelId}
          selectionMode="multiple"
          selectedKeys={new Set(shown.map(String))}
          onSelectionChange={(keys) => setDraft(keys === "all" ? tags.map((g) => g.id) : Array.from(keys, Number))}
        >
          {tags.map((g) => (
            <DropdownMenuItem key={g.id} id={String(g.id)} textValue={g.label}>
              <TagDot color={g.color} />
              {g.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </DropdownMenuTrigger>
    </div>
  );
}
